import type { HomePageInventoryItem } from '@app/data/clients/inventoryHydrationServiceClient/queries/types';
import { AnalyticsComponentName } from '@app/pages/i/analytics';
import { constants, PageSection, Text } from '@redbubble/design-system';
import { defineMessages, useIntl } from '@redbubble/next/intl';
import { UserInfo } from '@redbubble/next/types';
import {
  type Item as AnalyticsEcommerceItem,
  AnalyticsItemsList,
  AnalyticsItemsListIDNameMap,
} from '@redbubble/next-analytics/gtm';
import { useUserInfo } from '@redbubble/next-provider';
import {
  RSPageType,
  RSProductClickedLocation,
} from '@redbubble/next-provider/rudderstack';
import chunk from 'lodash/chunk';
import { useEffect, useMemo } from 'react';
import { CarouselSection } from '../../../../components/CarouselSection';
import {
  CarouselSectionContext,
  CarouselSectionContextValue,
} from '../../../../components/CarouselSection/carouselSectionContext';
import {
  parseHomePageCarouselItemToAnalyticsEcommerceItem,
  trackGA4ViewItemListHomePage,
} from '../../analytics';
import { getProductFeedSkeleton } from './ProductFeedSkeleton';
import {
  mapPriceProps,
  mapProductFeedInventoryItemToMediaCardProps,
  useFeaturedProducts,
} from './utils';

const messages = defineMessages({
  featuredProducts: {
    defaultMessage: 'Featured products',
  },
});

export const ProductFeed: React.FC = () => {
  const intl = useIntl();
  const [{ locale, country, currency }] = useUserInfo<UserInfo>();

  const {
    featuredProducts,
    loading: featuredProductsLoading,
    refresh: refreshFeaturedProducts,
  } = useFeaturedProducts();

  // biome-ignore lint/correctness/useExhaustiveDependencies: useExhaustiveDependencies
  useEffect(() => {
    refreshFeaturedProducts({
      locale,
      country,
      currency,
    });
  }, []);

  const contextValue: CarouselSectionContextValue = useMemo(() => {
    return {
      mediaCardPropsMapper: mapProductFeedInventoryItemToMediaCardProps(),
      pricePropsMapper: mapPriceProps,
      analytics: {
        componentName: AnalyticsComponentName.ProductFeed,
        inViewTracking: () => {
          const analyticsItems = featuredProducts
            .map((inventoryItem, index) => {
              if (!inventoryItem) return null;

              return parseHomePageCarouselItemToAnalyticsEcommerceItem({
                inventoryItem,
                index,
              });
            })
            .filter(
              (
                item: AnalyticsEcommerceItem | null
              ): item is NonNullable<AnalyticsEcommerceItem> => !!item
            );

          trackGA4ViewItemListHomePage({
            items: analyticsItems,
            listID: AnalyticsItemsList.ProductFeed,
            listName: AnalyticsItemsListIDNameMap.product_feed,
          });
        },
        listID: AnalyticsItemsList.ProductFeed,
      },
      rsLocation: RSProductClickedLocation.ProductFeed,
      rsPageType: RSPageType.Home,
    };
  }, [featuredProducts]);

  const getSlides = () => {
    const chunkSize = 2;
    if (featuredProductsLoading) {
      return getProductFeedSkeleton();
    }
    return chunk(featuredProducts, chunkSize).map(
      (products: HomePageInventoryItem[], outerIndex: number) => {
        return (
          <CarouselSection.CarouselSlide
            key={`pf-slide-${products
              .map((r) => {
                return r.id;
              })
              .join('+')}`}
          >
            {products.map((product, innerIndex) => (
              <CarouselSection.CarouselMediaCard
                data={product}
                key={product.id}
                index={innerIndex + chunkSize * outerIndex}
              />
            ))}
          </CarouselSection.CarouselSlide>
        );
      }
    );
  };

  if (!featuredProductsLoading && featuredProducts.length === 0) return null;

  return (
    <PageSection
      profile={constants.DESKTOP}
      size={constants.SMALL}
      marginTop={1}
      paddingTop={2}
      backgroundColor={constants.COLOR_WHITE}
    >
      <CarouselSectionContext.Provider value={contextValue}>
        <CarouselSection.Container>
          <Text block element="h2" type="display2">
            {intl.formatMessage(messages.featuredProducts)}
          </Text>
          <CarouselSection.Carousel
            itemsInView={{
              mobile: 2,
              tablet: 5,
              desktop: 5,
            }}
          >
            {getSlides()}
          </CarouselSection.Carousel>
        </CarouselSection.Container>
      </CarouselSectionContext.Provider>
    </PageSection>
  );
};
